import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { toast } from "sonner";
import { MessageCircle, Send, Loader2, Trash2, Shield, LogIn, Eye, X, Ban, CheckCircle2, Flag } from "lucide-react";
import { api as appApi } from "@/lib/api";

const MAX_CHARS = 800;

function Avatar({ src, name, size = 40 }) {
  const initials = (name || "?").split(" ").slice(0, 2).map((w) => w[0] || "").join("").toUpperCase();
  if (src) {
    return (
      <img
        src={src}
        alt={name || "avatar"}
        style={{ width: size, height: size }}
        className="rounded-full object-cover bg-slate-800 border border-slate-700 shrink-0"
        loading="lazy"
      />
    );
  }
  return (
    <div
      style={{ width: size, height: size }}
      className="rounded-full bg-gradient-to-br from-red-600/30 to-sky-600/30 border border-slate-700 flex items-center justify-center shrink-0 text-sm font-semibold text-slate-200"
    >
      {initials || "?"}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.round(diff / 60000);
    if (mins < 1) return "agora";
    if (mins < 60) return `${mins} min atrás`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `há ${hours}h`;
    const days = Math.round(hours / 24);
    if (days < 7) return `há ${days}d`;
    return d.toLocaleDateString("pt-BR");
  } catch {
    return iso;
  }
}

export default function CommentsSection({ episodeId }) {
  const { user, comments: api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [text, setText] = useState("");
  const [historyUser, setHistoryUser] = useState(null);

  const load = async () => {
    try {
      const d = await api.list(episodeId);
      setItems(d.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [episodeId]);

  const submit = async (e) => {
    e.preventDefault();
    const msg = text.trim();
    if (msg.length < 2) return;
    setPosting(true);
    try {
      const created = await api.create(episodeId, msg);
      setItems((prev) => [created, ...prev]);
      setText("");
      toast.success("Comentário publicado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Falha ao publicar");
    } finally {
      setPosting(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm("Remover este comentário?")) return;
    try {
      await api.remove(c.id);
      setItems((prev) => prev.filter((x) => x.id !== c.id));
      toast.success("Comentário removido");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Falha ao remover");
    }
  };

  const canModerate = user && user.is_moderator;

  return (
    <section className="space-y-6 pt-2" data-testid="comments-section">
      <div className="flex items-center gap-3 flex-wrap">
        <MessageCircle className="w-5 h-5 text-sky-400" />
        <h2 className="font-serif text-2xl font-bold text-white">
          Comentários {items.length > 0 && <span className="text-slate-500 text-base font-normal">({items.length})</span>}
        </h2>
      </div>

      <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-3 text-[11px] text-amber-200/80 leading-relaxed" data-testid="comments-policy">
        <strong className="text-amber-200">Política de convivência acadêmica:</strong> estimulamos debate rigoroso e respeitoso.
        Comentários com ofensas pessoais, discurso de ódio, spam, autopromoção agressiva ou conteúdo alheio ao contexto jurídico
        podem ser removidos pela editoria sem aviso prévio.
      </div>

      {/* Form */}
      {user ? (
        <form onSubmit={submit} className="space-y-2" data-testid="comment-form">
          <div className="flex items-start gap-3">
            <Avatar src={user.avatar} name={user.name} />
            <div className="flex-1 space-y-2">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))}
                placeholder={`Escreva um comentário (${MAX_CHARS} caracteres no máximo)…`}
                rows={3}
                maxLength={MAX_CHARS}
                className="w-full rounded-xl border border-slate-800 bg-slate-950 p-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500/60 focus:outline-none resize-none"
                data-testid="comment-input"
              />
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-slate-500">{text.length}/{MAX_CHARS}</span>
                <button
                  type="submit"
                  disabled={posting || text.trim().length < 2}
                  className="btn-primary !py-2 !text-xs disabled:opacity-50"
                  data-testid="comment-submit"
                >
                  {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Publicar
                </button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-4 text-sm text-slate-400 flex items-center gap-3" data-testid="comments-login-cta">
          <LogIn className="w-4 h-4 text-sky-400" />
          <span>Faça <Link to="/conta" className="text-sky-400 hover:underline">login</Link> para comentar.</span>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-slate-500 text-sm">Carregando comentários…</div>
      ) : items.length === 0 ? (
        <div className="text-slate-500 text-sm italic" data-testid="comments-empty">
          Ainda não há comentários. Seja a primeira pessoa a compartilhar uma reflexão sobre este episódio.
        </div>
      ) : (
        <ul className="space-y-4" data-testid="comments-list">
          {items.map((c) => {
            const canDelete = user && (user.id === c.user_id || user.is_moderator);
            return (
              <li key={c.id} className="flex items-start gap-3" data-testid={`comment-${c.id}`}>
                <Avatar src={c.user_avatar} name={c.user_name} />
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <span className="font-semibold text-white">{c.user_name}</span>
                    {c.user_is_moderator && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-wider bg-sky-500/10 border border-sky-500/30 text-sky-300" title="Moderador(a) da editoria">
                        <Shield className="w-2.5 h-2.5" /> Moderador(a)
                      </span>
                    )}
                    {c.user_institution && (
                      <span className="text-slate-500 truncate max-w-[12rem]">· {c.user_institution}</span>
                    )}
                    <span className="text-slate-600">· {formatDate(c.created_at)}</span>
                  </div>
                  <p className="text-sm text-slate-200 whitespace-pre-wrap break-words leading-relaxed">{c.message}</p>
                  <div className="flex items-center gap-3 pt-1">
                    {canDelete && (
                      <button
                        onClick={() => remove(c)}
                        className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-red-400 transition"
                        data-testid={`comment-delete-${c.id}`}
                      >
                        <Trash2 className="w-3 h-3" /> {user.id === c.user_id ? "Apagar meu comentário" : "Remover"}
                      </button>
                    )}
                    {canModerate && user.id !== c.user_id && (
                      <button
                        onClick={() => setHistoryUser({ id: c.user_id, name: c.user_name })}
                        className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-sky-400 transition"
                        data-testid={`comment-history-${c.id}`}
                        title="Ver todos os comentários deste usuário"
                      >
                        <Eye className="w-3 h-3" /> Ver histórico
                      </button>
                    )}
                    {user && user.id !== c.user_id && !c.user_is_moderator && (
                      <ReportButton commentId={c.id} testId={`comment-report-${c.id}`} />
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {canModerate && items.length > 0 && (
        <p className="text-[11px] text-sky-300/70 pt-2 border-t border-slate-800">
          <Shield className="inline w-3 h-3 mr-1" /> Você tem permissão de moderação para esta área.
        </p>
      )}

      {historyUser && (
        <UserHistoryModal
          userId={historyUser.id}
          userName={historyUser.name}
          onClose={() => setHistoryUser(null)}
          onCommentDeleted={(id) => setItems((prev) => prev.filter((x) => x.id !== id))}
        />
      )}
    </section>
  );
}

function UserHistoryModal({ userId, userName, onClose, onCommentDeleted }) {
  const { comments: api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [banning, setBanning] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    api.userHistory(userId)
      .then(setData)
      .catch((err) => {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Não foi possível carregar histórico");
        onClose();
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [userId]);

  const removeComment = async (c) => {
    if (!window.confirm("Remover este comentário?")) return;
    try {
      await api.remove(c.id);
      setData((prev) => prev ? { ...prev, items: prev.items.filter((x) => x.id !== c.id), total: prev.total - 1 } : prev);
      onCommentDeleted(c.id);
      toast.success("Comentário removido");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Falha ao remover");
    }
  };

  const toggleBan = async () => {
    const isBanned = !!data?.user?.comment_ban?.banned;
    const next = !isBanned;
    if (next && !reason.trim()) {
      toast.error("Descreva o motivo do banimento (fica registrado).");
      return;
    }
    if (!window.confirm(next ? "Impedir este usuário de comentar no JurisTube?" : "Liberar este usuário para comentar novamente?")) return;
    setBanning(true);
    try {
      await api.setBan(userId, next, reason);
      setData((prev) => prev ? {
        ...prev,
        user: {
          ...prev.user,
          comment_ban: next ? { banned: true, reason, at: new Date().toISOString() } : null,
        },
      } : prev);
      toast.success(next ? "Usuário impedido de comentar" : "Usuário liberado para comentar");
      if (!next) setReason("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Falha ao atualizar status");
    } finally {
      setBanning(false);
    }
  };

  const isBanned = !!data?.user?.comment_ban?.banned;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Histórico de comentários do usuário"
      data-testid="user-history-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3 p-5 border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar src={data?.user?.avatar} name={userName} size={44} />
            <div className="min-w-0">
              <h3 className="font-serif text-lg font-bold text-white truncate">{userName}</h3>
              <p className="text-xs text-slate-500 truncate">
                {data?.user?.email || "—"} · {data?.user?.institution || "sem instituição"}
              </p>
              <p className="text-[11px] text-slate-600">
                {data?.total ?? 0} comentário{(data?.total ?? 0) === 1 ? "" : "s"} no total
                {isBanned && <span className="ml-2 text-red-400 font-semibold">· banido de comentar</span>}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white shrink-0"
            data-testid="user-history-close"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="p-5 space-y-3 border-b border-slate-800 bg-slate-950/60 shrink-0">
          {isBanned ? (
            <>
              <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-200">
                <strong className="block mb-0.5">Usuário impedido de comentar.</strong>
                {data?.user?.comment_ban?.reason && <p>Motivo: {data.user.comment_ban.reason}</p>}
              </div>
              <button
                onClick={toggleBan}
                disabled={banning}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-semibold disabled:opacity-50"
                data-testid="user-history-unban"
              >
                {banning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                Liberar para comentar
              </button>
            </>
          ) : (
            <div className="space-y-2">
              <label className="block text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
                Impedir de comentar (soft ban)
              </label>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 300))}
                placeholder="Motivo (fica registrado) — ex: spam repetido, ofensas pessoais…"
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500"
                data-testid="user-history-ban-reason"
              />
              <button
                onClick={toggleBan}
                disabled={banning || !reason.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10 text-xs font-semibold disabled:opacity-40"
                data-testid="user-history-ban"
              >
                {banning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                Impedir de comentar
              </button>
              <p className="text-[10px] text-slate-500">
                O usuário continua podendo navegar, criar coleções e assinar podcasts — apenas não consegue publicar novos comentários.
              </p>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="user-history-list">
          {loading ? (
            <p className="text-sm text-slate-500">Carregando histórico…</p>
          ) : !data?.items?.length ? (
            <p className="text-sm text-slate-500 italic">Nenhum comentário no histórico.</p>
          ) : (
            data.items.map((c) => (
              <div key={c.id} className="rounded-xl border border-slate-800 bg-slate-950/60 p-3 space-y-1.5" data-testid={`history-comment-${c.id}`}>
                <div className="flex items-center justify-between gap-2 text-[11px]">
                  <Link
                    to={`/episodio/${c.episode_id}`}
                    target="_blank"
                    className="text-sky-400 hover:text-sky-300 hover:underline truncate max-w-[70%]"
                    title={c.episode_title}
                  >
                    {c.episode_title || "episódio"}
                  </Link>
                  <span className="text-slate-500">{formatDate(c.created_at)}</span>
                </div>
                <p className="text-sm text-slate-200 whitespace-pre-wrap break-words">{c.message}</p>
                <button
                  onClick={() => removeComment(c)}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-red-400 transition"
                  data-testid={`history-delete-${c.id}`}
                >
                  <Trash2 className="w-3 h-3" /> Remover
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function ReportButton({ commentId, testId }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (done) return;
    const reason = window.prompt(
      "Por que você está denunciando este comentário? (opcional, ajuda a moderação)",
      "",
    );
    // Cancel button returns null — abort silently.
    if (reason === null) return;
    setBusy(true);
    try {
      const r = await appApi.reportComment(commentId, reason || "");
      setDone(true);
      toast.success(
        r.already_reported
          ? "Você já tinha denunciado este comentário"
          : "Denúncia enviada — moderadores serão notificados",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao denunciar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={submit}
      disabled={busy || done}
      className={`inline-flex items-center gap-1 text-[11px] transition disabled:opacity-50 ${
        done ? "text-amber-300" : "text-slate-500 hover:text-amber-400"
      }`}
      data-testid={testId}
      title={done ? "Denúncia já enviada" : "Denunciar comentário inadequado"}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Flag className="w-3 h-3" />}
      {done ? "Denúncia registrada" : "Denunciar"}
    </button>
  );
}

